import { Component, computed, DestroyRef, inject, input, linkedSignal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { TaskDescriptionAcknowledgementsActionsService } from '@app/shared/services/actions/task-description-acknowledgements-actions.service';
import { AuthService } from '@app/shared/services/auth.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { TaskDescriptionAcknowledgementsDataService } from '@app/shared/services/task-description-acknowledgements-data.service';
import { Task } from '@app/shared/types/models/task/task.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY, finalize, switchMap } from 'rxjs';

/**
 * Checkbox letting the current user declare that they have read and acknowledged the task description.
 *
 * Checking creates an acknowledgement via {@link TaskDescriptionAcknowledgementsActionsService}.
 */
@Component({
  selector: 'proffeo-task-description-acknowledgement-checkbox',
  templateUrl: './task-description-acknowledgement-checkbox.component.html',
  host: {
    class: 'block w-full'
  },
  imports: [MatCheckboxModule, TranslatePipe]
})
export class TaskDescriptionAcknowledgementCheckboxComponent {
  private readonly acknowledgementsData = inject(TaskDescriptionAcknowledgementsDataService);
  private readonly acknowledgementsActions = inject(TaskDescriptionAcknowledgementsActionsService);
  private readonly authService = inject(AuthService);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isSubmitting = signal(false);
  protected readonly acknowledgements = computed(() => this.acknowledgementsData.getAcknowledgements(this.task().id)());

  protected readonly currentUserAcknowledgement = computed(() => {
    const currentUserId = this.authService.currentUser()?.id;
    if (currentUserId == null) {
      return undefined;
    }
    return this.acknowledgements().find(acknowledgement => acknowledgement.userId === currentUserId);
  });

  protected readonly hasCurrentUserAcknowledged: WritableSignal<boolean> = linkedSignal(
    () =>
      this.currentUserAcknowledgement()?.acceptedAt !== undefined &&
      this.currentUserAcknowledgement()?.acceptedAt !== null
  );
  protected readonly checkboxDisabled = computed(() => this.isSubmitting() || this.hasCurrentUserAcknowledged());
  protected readonly showCheckbox = computed(() => this.isSubmitting() || !this.hasCurrentUserAcknowledged());

  public readonly task = input.required<Task>();

  protected onAcknowledgementChange(event: MatCheckboxChange): void {
    if (!event.checked || this.checkboxDisabled()) {
      event.source.checked = this.hasCurrentUserAcknowledged();
      return;
    }

    event.source.checked = false;
    this.hasCurrentUserAcknowledged.set(false);

    this.dialogService
      .openConfirmationDialog({
        title: this.translate.instant('task-description-acknowledgement-panel.confirm-title'),
        desc: this.translate.instant('task-description-acknowledgement-panel.confirm-description')
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(confirmed => {
          if (confirmed !== true) {
            event.source.checked = false;
            this.hasCurrentUserAcknowledged.set(false);
            return EMPTY;
          }

          event.source.checked = true;
          this.hasCurrentUserAcknowledged.set(true);
          this.isSubmitting.set(true);
          return this.acknowledgementsActions.createAcknowledgement(this.task().id);
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe({
        error: () => {
          event.source.checked = false;
          this.hasCurrentUserAcknowledged.set(false);
          this.toastService.error(this.translate.instant('task-description-acknowledgement-panel.toast-error'));
        }
      });
  }
}
